
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Shop } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { networkService } from '@/services/network.service';
import { offlineDb } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { shopRepository } from '@/repositories/shop.repository';
import { associateListWithShop as associateListWithShopService } from '@/services/shops.service';
import { getValidUserId } from '@/utils/validation';

const convertToShop = (shopData: any): Shop => ({
  id: shopData.id,
  shop_name: shopData.shop_name,
  address: shopData.address,
  shop_logo: shopData.shop_logo,
  latitude: shopData.latitude,
  longitude: shopData.longitude,
  user_id: shopData.user_id || '',
  created_at: shopData.created_at || new Date().toISOString(),
  updated_at: shopData.updated_at || new Date().toISOString()
});

export const useShops = () => {
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = networkService.addListener((status) => {
      setIsOnline(status.connected);
    });
    return unsubscribe;
  }, []);

  // ✅ FIX: Migliorata visualizzazione negozi offline
  const { data: shops, isLoading, error } = useQuery({
    queryKey: ['user-shops'],
    queryFn: async (): Promise<Shop[]> => {
      if (networkService.isOnline()) {
        try {
          OfflineLogger.info('useShops', 'Fetching shops online');
          const { data, error } = await supabase
            .from('user_shops')
            .select('*')
            .order('shop_name');

          if (error) throw error;

          const shops = (data || []).map(convertToShop);
          OfflineLogger.info('useShops', `Found ${shops.length} shops online`);

          // Cache offline per negozi
          await Promise.all(shops.map(async (shop) => {
            try {
              await offlineDb.shops.put({
                id: shop.id,
                shop_name: shop.shop_name,
                address: shop.address,
                shop_logo: shop.shop_logo,
                latitude: shop.latitude,
                longitude: shop.longitude,
                user_id: shop.user_id,
                created_at: shop.created_at,
                updated_at: shop.updated_at,
                _syncStatus: 'synced' as const,
                _lastModified: Date.now()
              });
            } catch (err) {
              OfflineLogger.error('useShops.cacheShop', err);
            }
          }));

          OfflineLogger.info('useShops', `Cached ${shops.length} shops offline`);
          return shops;
        } catch (error) {
          OfflineLogger.error('useShops.fetchOnline', error);
          // Fallback offline
          const currentUserId = getValidUserId();
          if (!currentUserId) return [];
          const offlineShops = await offlineDb.shops
            .where('user_id')
            .equals(currentUserId)
            .and(shop => shop._syncStatus !== 'deleted')
            .toArray();
          OfflineLogger.info('useShops', `Using ${offlineShops.length} offline shops as fallback`);
          return offlineShops.map(convertToShop);
        }
      } else {
        // ✅ FIX: Modalità offline migliorata
        OfflineLogger.info('useShops', 'Using offline shops from local DB');
        const currentUserId = getValidUserId();
        if (!currentUserId) return [];
        const offlineShops = await offlineDb.shops
          .where('user_id')
          .equals(currentUserId)
          .and(shop => shop._syncStatus !== 'deleted')
          .toArray();
        OfflineLogger.info('useShops', `Found ${offlineShops.length} shops in offline storage`);
        return offlineShops.map(convertToShop);
      }
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // ✅ FIX: Aggiunta funzione createShop mancante
  const createShopMutation = useMutation({
    mutationFn: async (shopData: Omit<Shop, "id" | "user_id" | "created_at" | "updated_at">) => {
      if (networkService.isOnline()) {
        const user = await supabase.auth.getUser();
        if (!user.data?.user?.id) throw new Error("Utente non autenticato");

        const enrichedShop = {
          ...shopData,
          user_id: user.data.user.id
        };

        const { data, error } = await supabase
          .from('user_shops')
          .insert([enrichedShop])
          .select()
          .single();

        if (error) throw error;
        return convertToShop(data);
      } else {
        return await shopRepository.create(shopData); // offline già gestisce user_id
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-shops'] });
    }
  });

  // ✅ FIX: Aggiunta funzione updateShop mancante
  const updateShopMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Shop> }) => {
      if (networkService.isOnline()) {
        const { data, error } = await supabase
          .from('user_shops')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;
        return convertToShop(data);
      } else {
        return await shopRepository.update(id, updates);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-shops'] });
    }
  });

  // ✅ FIX: Aggiunta funzione deleteShop mancante
  const deleteShopMutation = useMutation({
    mutationFn: async (id: string) => {
      if (networkService.isOnline()) {
        const { error } = await supabase
          .from('user_shops')
          .delete()
          .eq('id', id);

        if (error) throw error;
      } else {
        await shopRepository.delete(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-shops'] });
    }
  });

  // ✅ FIX: Aggiunta funzione mancante per associare lista con negozio
  const associateListWithShop = async ({ listId, shopId }: { listId: string; shopId: string | null }): Promise<boolean> => {
    const result = await associateListWithShopService(listId, shopId);

    if (result) {
      queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
      queryClient.invalidateQueries({ queryKey: ['primary-list'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
    }

    return result;
  };

  return {
    shops: shops || [],
    isLoading,
    error,
    isOnline,
    associateListWithShop,
    // ✅ FIX: Aggiunte funzioni mancanti
    createShop: createShopMutation.mutateAsync,
    isCreatingShop: createShopMutation.isPending,
    updateShop: updateShopMutation.mutateAsync,
    deleteShop: deleteShopMutation.mutateAsync,
    isLoadingShops: isLoading,
    useCategoryOrdering: (shopId: string | null) => {
      return useQuery({
        queryKey: ['category-ordering', shopId],
        queryFn: async () => {
          if (!shopId) return [];
          if (!networkService.isOnline()) {
            const records = await offlineDb.categoryShopOrdering
              .where('shop_id')
              .equals(shopId)
              .sortBy('position');
            return records.map(r => ({ category_id: r.category_id, position: r.position, visibile: r.visibile !== false }));
          }

          const { data, error } = await supabase
            .from('user_categories_shops')
            .select('category_id, position, visibile')
            .eq('shop_id', shopId)
            .order('position');

          if (error) throw error;
          return (data || []).map(d => ({ ...d, visibile: d.visibile !== false }));
        },
        enabled: !!shopId
      });
    }
  };
};

// ✅ FIX: Esportiamo anche useCategoryOrdering come funzione standalone
export const useCategoryOrdering = (shopId: string | null) => {

  return useQuery({
    queryKey: ['category-ordering', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      if (!networkService.isOnline()) {
        const records = await offlineDb.categoryShopOrdering
          .where('shop_id')
          .equals(shopId)
          .sortBy('position');
        return records.map(r => ({ category_id: r.category_id, position: r.position, visibile: r.visibile !== false }));
      }

      const { data, error } = await supabase
        .from('user_categories_shops')
        .select('category_id, position, visibile')
        .eq('shop_id', shopId)
        .order('position');

      if (error) throw error;
      return (data || []).map(d => ({ ...d, visibile: d.visibile !== false }));
    },
    enabled: !!shopId
  });
};
